export const LIVE_ANALYTICS_EVENTS=Object.freeze([
  'live_session_started',
  'live_session_completed',
  'live_session_cancelled',
  'stage_started',
  'stage_completed',
  'stage_skipped',
  'temperature_logged',
  'event_logged',
  'notification_interaction'
]);

const ALLOWED=new Set(LIVE_ANALYTICS_EVENTS);
const STAGE_REASONS=new Set(['initial','advance','skip','reopen','custom_insert','terminal']);
const READING_KINDS=new Set(['chamber','internal']);
const SOURCE_TYPES=new Set(['manual','thermometer_probe','device']);
const EVENT_TYPES=new Set(['wood_added','chamber_opened','stage_changed','product_checked','custom']);
const NOTIFICATION_KINDS=new Set(['stage_end','temperature_check']);

function bool(value){return Boolean(value);}

function count(value,max=1000){
  const number=Number(value);
  if(!Number.isFinite(number))return 0;
  return Math.max(0,Math.min(max,Math.floor(number)));
}

function choice(value,allowed,fallback='unknown'){
  const key=String(value||'').trim().toLowerCase();
  return allowed.has(key)?key:fallback;
}

function propertiesFor(name,input={}){
  switch(name){
    case 'live_session_started':
    case 'live_session_completed':
    case 'live_session_cancelled':
      return Object.freeze({offline:bool(input.offline)});
    case 'stage_started':
    case 'stage_completed':
      return Object.freeze({
        reason:choice(input.reason,STAGE_REASONS),
        offline:bool(input.offline)
      });
    case 'stage_skipped':
      return Object.freeze({
        reason:'skip',
        skipped_count:count(input.skippedCount,100),
        offline:bool(input.offline)
      });
    case 'temperature_logged':
      return Object.freeze({
        reading_kind:choice(input.readingKind,READING_KINDS),
        source_type:choice(input.sourceType,SOURCE_TYPES),
        offline:bool(input.offline)
      });
    case 'event_logged':
      return Object.freeze({
        event_type:choice(input.eventType,EVENT_TYPES),
        offline:bool(input.offline)
      });
    case 'notification_interaction':
      return Object.freeze({
        notification_kind:choice(input.kind,NOTIFICATION_KINDS),
        action:String(input.action||'').trim().toLowerCase()==='tap'?'tap':'other'
      });
    default:
      throw new TypeError('Unsupported Live analytics event');
  }
}

export function createLiveAnalytics({analytics=null}={}){
  function track(name,input={}){
    if(!ALLOWED.has(name))throw new TypeError('Unsupported Live analytics event');
    const properties=propertiesFor(name,input);
    try{
      const result=analytics?.track?.(name,properties);
      return result??Object.freeze({accepted:false,reason:'ANALYTICS_UNAVAILABLE'});
    }catch{
      return Object.freeze({accepted:false,reason:'ANALYTICS_FAILED'});
    }
  }

  return Object.freeze({
    track,
    sessionStarted:input=>track('live_session_started',input),
    sessionCompleted:input=>track('live_session_completed',input),
    sessionCancelled:input=>track('live_session_cancelled',input),
    stageStarted:input=>track('stage_started',input),
    stageCompleted:input=>track('stage_completed',input),
    stageSkipped:input=>track('stage_skipped',input),
    temperatureLogged:input=>track('temperature_logged',input),
    eventLogged:input=>track('event_logged',input),
    notificationInteraction:input=>track('notification_interaction',input)
  });
}
